import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, FieldAPI, FormAPI, InputAPI, ModalProps, RadioGroupAPI } from '@philips-internal/oswald-ui-components/lib/components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ModalService, SelectableItem, ToastService } from '@philips-internal/oswald-ui-components';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { generateUUID } from '@philips-internal/oswald-ui-utils';

import { Inject } from '@odinjs/odin';
import moment from 'moment';

import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import BaseController from '../../../BaseController';
import DialogService from '../../../../services/dialog-service';
import { FragmentNames, HealthHistorySocialHistoryViewerAliases } from '../../../../codegen';
import { Endpoint } from '../../../../constants/endpoints';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { HealthHistorySocialHistoryRequest, HealthHistorySocialHistoryResponse, HealthHistoryType } from '../../health-history-types';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import { SocialHistoryConditionId, SocialHistoryRadioGroupId } from '../social-history-types';
import { ApplicationRoute } from '../../../../constants/routes';
import { YesNoType } from '../../../../utils/enum-utils';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { FunctionName, PEP_281Parameters } from '../../../../utils/parameters';
import { FULL_LOCALE_DATE } from '../../../../constants/date-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';

enum ConditionRadioGroup {
  MILD_LITTLE = 0,
  MEDIUM_MODERATE = 1,
  SEVERE_LARGE = 2,
}

enum VerifyReviewParameter {
  ALLOWED_REVIEW = 'S',
  NOT_ALLOWED_REVIEW = 'N'
}

@Controller({  component: HealthHistorySocialHistoryViewerAliases.LAYOUT_HEALTH_HISTORY_SOCIAL_HISTORY_VIEWER, fragment: FragmentNames.HEALTH_HISTORY_SOCIAL_HISTORY_VIEWER })
export default class HealthHistorySocialHistoryViewerController extends BaseController {

  @Inject(HealthHistorySocialHistoryViewerAliases.ACTION_NEW_HEALTH_HISTORY_SOCIAL_HISTORY)
  private actionNewHealthHistoryAPI!: ActionAPI

  @Inject(HealthHistorySocialHistoryViewerAliases.ACTION_INACTIVATE_HEALTH_HISTORY_SOCIAL_HISTORY)
  private actionInactivateHealthHistoryAPI!: ActionAPI

  @Inject(HealthHistorySocialHistoryViewerAliases.ACTION_REVIEW_HEALTH_HISTORY_SOCIAL_HISTORY)
  private actionReviewHealthHistoryAPI!: ActionAPI

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_PROFESSIONAL)
  private inputProfessionalAPI!: InputAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_DENIES_HABITS)
  private fieldDeniesHabitsAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_DISPLAY_PATIENT_ALERTS)
  private fieldDisplayPatientAlertsAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_CLASSIFICATION)
  private fieldClassificationAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_TYPE)
  private fieldTypeAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_BRAND)
  private fieldBrandAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_UNIT_MEASURE)
  private fieldUnitMeasureAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_QUANTITY)
  private fieldQuantityAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_DESCRIPTIVE_QUANTITY)
  private fieldDescriptiveQuantityAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_FREQUENCY)
  private fieldFrequencyAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_SECURITY_LEVEL)
  private fieldSecurityLevelAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_AGE_EXPERIENCE)
  private fieldAgeExperienceAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_AGE_REGULAR_USE)
  private fieldAgeRegularUseAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_TOTAL_PACKS)
  private fieldTotalPacksAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_START_DATE)
  private fieldStartDateAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_END_DATE)
  private fieldEndDateAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_DURATION)
  private fieldDurationAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_INSERT_LIST_PROBLEMS)
  private fieldInsertListProblemsAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_CONSEQUENCES)
  private fieldConsequencesAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_FAMILY_ANTECEDENTS)
  private fieldFamilyAntecedentsAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_NOTES)
  private fieldNotesAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FORM_HEALTH_HISTORY_SOCIAL_HISTORY_VIEWER)
  private formHealthHistorySocialHistoryAPI!: FormAPI

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_REVIEW_DATE)
  private fieldReviewDateAPI!: FieldAPI;

  @Inject(HealthHistorySocialHistoryViewerAliases.FIELD_REVIEW_USERNAME)
  private fieldReviewUsernameAPI!: FieldAPI;

  @OnMount({ target: HealthHistorySocialHistoryViewerAliases.LAYOUT_HEALTH_HISTORY_SOCIAL_HISTORY_VIEWER })
  handleMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
    HealthHistoryListContext.currentTab = HealthHistoryType.SOCIAL_HISTORY;
  }

  @OnMount({ target: HealthHistorySocialHistoryViewerAliases.FORM_HEALTH_HISTORY_SOCIAL_HISTORY_VIEWER })
  handleMountForm(): void {
    this.formHealthHistorySocialHistoryAPI.setLoading(true);
    this.fetchHealthHistorySocialHistory();
  }

  @OnMount({ target: HealthHistorySocialHistoryViewerAliases.RADIO_GROUP_CONDITION })
  handleMountRadioGroupCondition(event: MountEvent): void {
    const radioGroup = event.target as RadioGroupAPI;
    this.setRadioGroupValues(SocialHistoryRadioGroupId.CONDITION, radioGroup);
  }

  @OnClick({ target: HealthHistorySocialHistoryViewerAliases.ACTION_REVIEW_HEALTH_HISTORY_SOCIAL_HISTORY })
  handleClickReview(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.confirmReview(idHealthHistory as number);
  }

  @OnClick({ target: HealthHistorySocialHistoryViewerAliases.ACTION_NEW_HEALTH_HISTORY_SOCIAL_HISTORY })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.HEALTH_HISTORY_SOCIAL_HISTORY_CREATE, {
      encounterId,
      patientId,
    });
  }

  @OnClick({ target: HealthHistorySocialHistoryViewerAliases.ACTION_INACTIVATE_HEALTH_HISTORY_SOCIAL_HISTORY })
  handleCLickInactivate(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'health-history-inactivate',
      id: 'health-history-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      idHealthHistory,
    } as Record<string, Value>;

    ModalService.openDefault(modalProperties);
  }

  @OnUnmount(HealthHistorySocialHistoryViewerAliases.LAYOUT_HEALTH_HISTORY_SOCIAL_HISTORY_VIEWER)
  handleUnmount(): void {
    HealthHistoryListContext.clean();
    HealthHistoryListContext.currentTab = HealthHistoryType.SOCIAL_HISTORY;
  }

  private getParamAccess(module: string, parameter: string): string {
    return FoundationUtils.findModuleParam(module, parameter) as string;
  }

  private defineEnablingReviewItem(): void {
    const isEnabledReview = this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.ALLOW_REVIEW_HEALTH_HISTORY);
    const allowInfo = isEnabledReview === VerifyReviewParameter.ALLOWED_REVIEW && InformationBarUtils.allowInfo && HealthHistoryListContext.isAllowEdit;
    this.actionReviewHealthHistoryAPI.setEnabled(allowInfo);
  }

  private handleVisibleFields(record: HealthHistorySocialHistoryResponse): void {
    this.fieldDeniesHabitsAPI.setVisible(record.deniesHabits !== YesNoType.NO);
    this.fieldDisplayPatientAlertsAPI.setVisible(record.alert != null);
    this.fieldClassificationAPI.setVisible(record.classification?.description != null);
    this.fieldTypeAPI.setVisible(record.type?.description != null);
    this.fieldBrandAPI.setVisible(record.brand?.description != null);
    this.fieldUnitMeasureAPI.setVisible(record.unitMeasure?.description != null);
    this.fieldQuantityAPI.setVisible(record.amount != null);
    this.fieldDescriptiveQuantityAPI.setVisible(record.descriptiveQuantity != null);
    this.fieldFrequencyAPI.setVisible(record.frequency?.description != null);
    this.fieldSecurityLevelAPI.setVisible(record.securityLevel?.description != null);
    this.fieldAgeExperienceAPI.setVisible(record.ageExperience != null);
    this.fieldAgeRegularUseAPI.setVisible(record.ageRegularUse != null);
    this.fieldTotalPacksAPI.setVisible(record.totalPacks != null);
    this.fieldStartDateAPI.setVisible(record.startDate != null);
    this.fieldEndDateAPI.setVisible(record.endDate != null);
    this.fieldDurationAPI.setVisible(record.duration != null);
    this.fieldInsertListProblemsAPI.setVisible(record.problemList !== YesNoType.NO)
    this.fieldConsequencesAPI.setVisible(record.consequences != null);
    this.fieldFamilyAntecedentsAPI.setVisible(record.antecedents != null);
    this.fieldNotesAPI.setVisible(record.notes != null);
    this.fieldReviewDateAPI.setVisible(record.reviewDate != null);
    this.fieldReviewUsernameAPI.setVisible(record.reviewUsername != null);
  }

  private fetchHealthHistorySocialHistory(): Promise<void> {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    const allowInfo = InformationBarUtils.allowInfo && HealthHistoryListContext.isAllowEdit;

    return HttpService.get(Endpoint.HEALTH_HISTORY_SOCIAL_HISTORY, { sequenceNumberSocialHistory: idHealthHistory })
      .then(res => {
        const record = res.body as HealthHistorySocialHistoryResponse;
        const user = foundationContext.getUser() as HealthProfessionalUser;
        const formData = this.convertToValidType(record);
        const condition = { key: record.condition, label: '', value: record.condition };
        const showInactivate = record.professional.id === user.physicalPersonId && Boolean(record.showInactivate) && allowInfo;

        this.inputProfessionalAPI.setValue(record.professional?.description as string)
        this.context.set({ socialHistory: formData });
        this.formHealthHistorySocialHistoryAPI.setEditing(true);
        this.formHealthHistorySocialHistoryAPI.validate();
        this.formHealthHistorySocialHistoryAPI.setDisabled(true);
        this.formHealthHistorySocialHistoryAPI.setData({ ...formData, condition });
        this.actionInactivateHealthHistoryAPI.setEnabled(showInactivate);

        this.handleVisibleFields(record);

      }).catch((error) =>
        error?.body?.errorMessage && this.showToast(error.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formHealthHistorySocialHistoryAPI.setLoading(false);
        this.actionNewHealthHistoryAPI.setEnabled(allowInfo);
        this.defineEnablingReviewItem();
      });
  }

  private convertToValidType(healthHistorySocialHistory: HealthHistorySocialHistoryResponse): HealthHistorySocialHistoryRequest {
    return {
      ageExperience: healthHistorySocialHistory.ageExperience || '',
      ageRegularUse: healthHistorySocialHistory.ageRegularUse || '',
      alert: BooleanUtils.isTrueValue(healthHistorySocialHistory.alert),
      amount: healthHistorySocialHistory.amount || '',
      antecedents: healthHistorySocialHistory.antecedents || '',
      brandDescription: healthHistorySocialHistory.brand ? healthHistorySocialHistory.brand.description : '',
      classificationDescription: healthHistorySocialHistory.classification ? healthHistorySocialHistory.classification.description : '',
      condition: healthHistorySocialHistory.condition || '',
      consequences: healthHistorySocialHistory.consequences || '',
      deniesHabits: BooleanUtils.isTrueValue(healthHistorySocialHistory.deniesHabits),
      descriptiveQuantity: healthHistorySocialHistory.descriptiveQuantity || '',
      duration: healthHistorySocialHistory.duration || '',
      endDate: healthHistorySocialHistory.endDate ? moment(healthHistorySocialHistory.endDate).utc().format('L') : '',
      frequencyDescription: healthHistorySocialHistory.frequency ? healthHistorySocialHistory.frequency.description : '',
      idHealthHistorySocialHistory: healthHistorySocialHistory.idHealthHistory,
      notes: healthHistorySocialHistory.notes || '',
      problemList: BooleanUtils.isTrueValue(healthHistorySocialHistory.problemList),
      professionalName: healthHistorySocialHistory.professional ? healthHistorySocialHistory.professional.description : '',
      recordDate: healthHistorySocialHistory.recordDate ? moment(healthHistorySocialHistory.recordDate).local().format('L LTS') : '',
      reviewDate: healthHistorySocialHistory?.reviewDate ? moment(healthHistorySocialHistory.reviewDate).local().format(FULL_LOCALE_DATE) : '',
      reviewUsername: healthHistorySocialHistory?.reviewUsername || '',
      securityLevelDescription: healthHistorySocialHistory.securityLevel ? healthHistorySocialHistory.securityLevel.description : '',
      startDate: healthHistorySocialHistory.startDate ? moment(healthHistorySocialHistory.startDate).utc().format('L') : '',
      totalPacks: healthHistorySocialHistory.totalPacks || '',
      typeDescription: healthHistorySocialHistory.type ? healthHistorySocialHistory.type.description : '',
      unitMeasureDescription: healthHistorySocialHistory.unitMeasure ? healthHistorySocialHistory.unitMeasure.description : '',
    }
  }

  private setRadioGroupValues(expressionId: number, radioGroup: RadioGroupAPI): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);
        const { condition } = this.context.get('socialHistory') as HealthHistorySocialHistoryRequest;
        switch (condition) {
          case SocialHistoryConditionId.MILD_LITTLE:
            return radioGroup.setValue(responseBody[ConditionRadioGroup.MILD_LITTLE]);
          case SocialHistoryConditionId.MEDIUM_MODERATE:
            return radioGroup.setValue(responseBody[ConditionRadioGroup.MEDIUM_MODERATE]);
          case SocialHistoryConditionId.SEVERE_LARGE:
            return radioGroup.setValue(responseBody[ConditionRadioGroup.SEVERE_LARGE]);
          default:
            return radioGroup.setValue(responseBody[ConditionRadioGroup.MILD_LITTLE]);
        }
      }).catch((error) => {
        error?.body?.errorMessage && this.showToast(error.body.errorMessage, ToastLevel.ERROR)
      });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-social-history-viewer',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
  }

  private review(idHealthHistory: number): void {
    const sequenceNumberSocialHistory = idHealthHistory;
    this.formHealthHistorySocialHistoryAPI.setLoading(true);
    HttpService.put(Endpoint.REVIEW_HEALTH_HISTORY_SOCIAL_HISTORY, { sequenceNumberSocialHistory })
      .then(() => {
        this.formHealthHistorySocialHistoryAPI.setLoading(false);
        this.goBack();
        this.showToast('text.successfully-reviewed', ToastLevel.SUCCESS);
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR);
        this.formHealthHistorySocialHistoryAPI.setLoading(false);
      });
  }

  private confirmReview(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: '',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.review(idHealthHistory); },
      titleDialog: 'text.confirm-review-item',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'text.review-item-hh',
    });
  }
}
